import React, { useState, useEffect } from "react";
import FileUpload from "./FileUpload.jsx";

function AdminExportsForm({ onSubmit, disabled, onUserChange, onDocumentUpload, uploadingDocs = {}, initialValues, existingDocuments = {}, resetKey, customerInfo, ensureDraft}) {
  const defaultFormData = {
    city: "LHR",
    transportMode: "air",
    containerType: "LCL",
    clearingAgentNum: "",
    files: {},
  };

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [uploadMessages, setUploadMessages] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const updateFormData = (updater, options = { notify: true }) => {
    setFormData((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (options.notify && onUserChange) {
        onUserChange(next);
      }
      return next;
    });
  };

  useEffect(() => {
    if (initialValues) {
      updateFormData(initialValues, { notify: false });
    }
  }, [initialValues]);

  useEffect(() => {
    if (resetKey) {
      updateFormData(defaultFormData, { notify: false });
      setErrors({});
      setUploadMessages({});
      setUploadProgress({});
    }
  }, [resetKey]);

  const renderProgress = (fileType) => {
    const progress = uploadProgress[fileType];
    if (progress == null) return null;
    const isComplete = progress >= 100;
    return (
      <div style={{ marginTop: "6px" }}>
        <div style={{ height: "6px", background: "#e2e8f0", borderRadius: "4px", overflow: "hidden" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: isComplete ? "#16a34a" : "#2563eb",
              transition: "width 0.2s ease",
            }}
          />
        </div>
        <div style={{ fontSize: "12px", color: isComplete ? "#16a34a" : "#2563eb", marginTop: "4px" }}>
          {isComplete ? "Upload complete" : `Uploading... ${progress}%`}
        </div>
      </div>
    );
  };

  const commitUserChanges = (next) => {
    if (onUserChange) {
      onUserChange(next ?? formData);
    }
  };

  const requiredFields = [
    "commonInvoices",
    "packingLists",
    "billOfLading",
    "pol",
    "pod",
    "product",
    "incoTerms",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData.files[field] || formData.files[field] === "") {
        newErrors[field] = `${getLabel(field)} is required`;
      }
    });

    if (formData.clearingAgentNum) {
      const digitsOnly = formData.clearingAgentNum.replace(/\D/g, "");
      if (digitsOnly.length !== 11) {
        newErrors.clearingAgentNum = "Clearing agent number must be exactly 11 digits";
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // include customer info
    onSubmit({ ...formData, customer: customerInfo });
  };

  const handleFileChange = async (fileType, file, existingDocId) => {
     // Ensure draft exists before uploading
    if (file && ensureDraft) {
      try {
        await ensureDraft();  // this will create draft and notify parent of userCreated/password
      } catch (err) {
        console.error('Failed to create draft', err);
        return;
      }
    }
    updateFormData((prev) => ({ ...prev, files: { ...prev.files, [fileType]: file } }));
    setErrors((prev) => ({ ...prev, [fileType]: "" }));

    if (file && onDocumentUpload) {
      try {
        setUploadMessages((prev) => ({ ...prev, [fileType]: "" }));
        setUploadProgress((prev) => ({ ...prev, [fileType]: 0 }));
        await onDocumentUpload(file, fileType, existingDocId, (percent) => {
          setUploadProgress((prev) => ({ ...prev, [fileType]: percent }));
        });
        setUploadMessages((prev) => ({ ...prev, [fileType]: "Upload complete" }));
        setUploadProgress((prev) => ({ ...prev, [fileType]: 100 }));
      } catch (err) {
        setUploadMessages((prev) => ({ ...prev, [fileType]: `Upload failed: ${err.message}` }));
        setUploadProgress((prev) => { const updated = { ...prev }; delete updated[fileType]; return updated; });
        setErrors((prev) => ({ ...prev, [fileType]: err.message }));
        updateFormData((prev) => { const newFiles = { ...prev.files }; delete newFiles[fileType]; return { ...prev, files: newFiles }; });
      }
    } else if (file) {
      setUploadMessages((prev) => ({ ...prev, [fileType]: "File selected" }));
    }
  };

  const handleTextChange = (field, value) => {
    updateFormData((prev) => ({ ...prev, files: { ...prev.files, [field]: value } }), { notify: false });
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleTextBlur = (field, value) => {
    const next = { ...formData, files: { ...formData.files, [field]: value } };
    updateFormData(next, { notify: false });
    commitUserChanges(next);
  };

  const handleClearingAgentNumChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, "");
    const limitedValue = digitsOnly.slice(0, 11);
    updateFormData((prev) => ({ ...prev, clearingAgentNum: limitedValue }), { notify: false });
    if (errors.clearingAgentNum) setErrors((prev) => ({ ...prev, clearingAgentNum: "" }));
  };

  const handleClearingAgentNumBlur = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, "");
    const limitedValue = digitsOnly.slice(0, 11);
    const next = { ...formData, clearingAgentNum: limitedValue };
    updateFormData(next, { notify: false });
    commitUserChanges(next);
  };

  const getDocumentList = () => [
    "commonInvoices",
    "packingLists",
    "billOfLading",
    "insurance",
    "pol",
    "pod",
    "product",
    "incoTerms",
  ];

  const getLabel = (docType) => {
    const labels = {
      commonInvoices: "Commercial Invoices",
      packingLists: "Packing Lists",
      billOfLading: "Bill Of Lading",
      insurance: "Insurance",
      pol: "Port of Loading",
      pod: "Port of Discharge",
      product: "Product/Items",
      incoTerms: "Incoterms",
      others: "Others",
    };
    return labels[docType] || docType;
  };

  const isTextField = (docType) => ["pol", "pod", "product", "incoTerms"].includes(docType);

  const textFields = getDocumentList().filter(isTextField);
  const fileFields = getDocumentList().filter((doc) => !isTextField(doc));
  const requiredFieldsSet = new Set(requiredFields);

  return (
    <form onSubmit={handleSubmit} className="space-y-6 frm-elem">
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-6 flex items-center space-x-2">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>Export Documentation</span>
        </h3>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Select City</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, city: "LHR", containerType: "LCL", transportMode: "air" }))} className={`px-6 py-3 rounded-full font-medium ${formData.city === "LHR" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>LHR</button>
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, city: "KHI" }))} className={`ml-1 px-6 py-3 rounded-full font-medium ${formData.city === "KHI" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>KHI</button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Transport Mode</label>
          {formData.city === "LHR" ? (
            <div className="radio-option">
              <input type="radio" id="exp-air-only" name="exp-transport" value="air" checked={formData.transportMode === "air"} onChange={(e) => updateFormData((prev) => ({ ...prev, transportMode: e.target.value }))} />
              <label htmlFor="exp-air-only">By air only</label>
            </div>
          ) : (
            <div>
              <div className="radio-option">
                <input type="radio" id="exp-air" name="exp-transport" value="air" checked={formData.transportMode === "air"} onChange={(e) => updateFormData((prev) => ({ ...prev, transportMode: e.target.value }))} />
                <label htmlFor="exp-air">By Air</label>
              </div>
              <div className="radio-option">
                <input type="radio" id="exp-sea" name="exp-transport" value="sea" checked={formData.transportMode === "sea"} onChange={(e) => updateFormData((prev) => ({ ...prev, transportMode: e.target.value }))} />
                <label htmlFor="exp-sea">By Sea</label>
              </div>
            </div>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Container Type</label>
          <div className="inline-flex rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => updateFormData((prev) => ({ ...prev, containerType: "LCL" }))} className={`px-6 py-3 rounded-full font-medium ${formData.containerType === "LCL" ? "bg-[#2c3e50] text-white" : "text-gray-700"}`}>LCL</button>
            <button type="button" disabled={formData.city === "LHR"} onClick={() => updateFormData((prev) => ({ ...prev, containerType: "FCL" }))} className={`ml-1 px-6 py-3 rounded-full font-medium ${formData.containerType === "FCL" ? "bg-[#2c3e50] text-white" : "text-gray-700"} ${formData.city === "LHR" ? "opacity-50 cursor-not-allowed" : ""}`}>FCL</button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-3">Clearing Agent Number</label>
          <input type="text" value={formData.clearingAgentNum} onChange={handleClearingAgentNumChange} onBlur={handleClearingAgentNumBlur} placeholder="Enter 11-digit clearing agent number" maxLength={11} inputMode="numeric" pattern="[0-9]{11}" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500" style={{ padding: "8px 10px" }} />
          {errors.clearingAgentNum && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors.clearingAgentNum}</p>}
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Required Documents</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {textFields.map((docType) => (
              <div key={docType}>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {getLabel(docType)}
                  {requiredFieldsSet.has(docType) && <span className="text-red-500"> *</span>}
                </label>
                <input type="text" value={formData.files[docType] || ""} onChange={(e) => handleTextChange(docType, e.target.value)} onBlur={(e) => handleTextBlur(docType, e.target.value)} className="w-full border rounded-lg p-2 text-sm border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500" placeholder={docType === "incoTerms" ? "e.g: FOB, CFR, Ex-Works, etc" : `Enter ${getLabel(docType)}`} />
                {errors[docType] && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors[docType]}</p>}
              </div>
            ))}

            {fileFields.map((docType) => (
              <div key={docType}>
                {existingDocuments[docType] && !formData.files[docType] && (
                  <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments[docType].fileName || 'Document'}{' '}
                    {existingDocuments[docType].signedUrl || existingDocuments[docType].fileUrl ? (
                      <a href={existingDocuments[docType].signedUrl || existingDocuments[docType].fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                    ) : null}
                  </div>
                )}
                <FileUpload label={<>{getLabel(docType)}{requiredFieldsSet.has(docType) && <span className="text-red-500"> *</span>}</>} onChange={(file) => handleFileChange(docType, file, existingDocuments[docType]?.id)} selectedFile={formData.files[docType]} />
                {renderProgress(docType)}
                {uploadingDocs[docType] && <p className="text-blue-600 text-sm mt-1">Uploading...</p>}
                {uploadMessages[docType] && !uploadingDocs[docType] && <p className="text-green-600 text-sm mt-1 success-msge" style={{ color: 'green' }}>{uploadMessages[docType]}</p>}
                {errors[docType] && <p className="text-red-500 text-xs mt-1 error-msg" style={{ color: 'red' }}>{errors[docType]}</p>}
              </div>
            ))}

            <div>
              {existingDocuments.others && !formData.files.others && (
                <div className="text-xs text-slate-600 mb-1">Current: {existingDocuments.others.fileName || 'Document'}{' '}
                  {existingDocuments.others.signedUrl || existingDocuments.others.fileUrl ? (
                    <a href={existingDocuments.others.signedUrl || existingDocuments.others.fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Download</a>
                  ) : null}
                </div>
              )}
              <FileUpload label={getLabel("others")} onChange={(file) => handleFileChange("others", file, existingDocuments.others?.id)} selectedFile={formData.files.others} />
              {renderProgress("others")}
            </div>
          </div>
        </div>

        <button type="submit" disabled={disabled} className="w-full btn-modal-sub bg-slate-600 hover:bg-slate-800 text-white py-3 px-6 rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed">{disabled ? "Submitting..." : "Submit Export Documentation"}</button>
      </div>
    </form>
  );
}

export default AdminExportsForm;
